import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DeleteSweep, Send, Stop } from "@mui/icons-material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { getAiStatus, streamAiQuery } from "../features/ai/aiService";

let messageId = 0;

const HISTORY_STORAGE_KEY = "pba_ai_chat_history";
const HISTORY_MAX_MESSAGES = 50;

/** Restore completed turns from localStorage (best-effort). */
function loadHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (m) =>
          m &&
          (m.role === "user" || m.role === "assistant") &&
          typeof m.content === "string",
      )
      .map((m) => ({
        id: ++messageId,
        role: m.role,
        content: m.content,
        status: "done",
        error: null,
      }));
  } catch {
    return [];
  }
}

// Markdown element mapping for assistant replies - keeps tables, lists and
// code blocks theme-consistent instead of browser-default styling.
const markdownComponents = {
  p: ({ children }) => (
    <Typography variant="body2" sx={{ mb: 1 }}>
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ pl: 3, my: 1, mt: 0 }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ pl: 3, my: 1, mt: 0 }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
      {children}
    </Typography>
  ),
  table: ({ children }) => (
    <Box sx={{ overflowX: "auto", my: 1 }}>
      <Table size="small" sx={{ minWidth: 320 }}>
        {children}
      </Table>
    </Box>
  ),
  thead: ({ children }) => <TableHead>{children}</TableHead>,
  tbody: ({ children }) => <TableBody>{children}</TableBody>,
  tr: ({ children }) => <TableRow>{children}</TableRow>,
  th: ({ children }) => (
    <TableCell sx={{ fontWeight: 700, fontSize: "0.8rem" }}>{children}</TableCell>
  ),
  td: ({ children }) => (
    <TableCell sx={{ fontSize: "0.8rem" }}>{children}</TableCell>
  ),
  pre: ({ children }) => (
    <Box
      component="pre"
      sx={{
        bgcolor: "action.hover",
        borderRadius: 1,
        p: 1.5,
        overflowX: "auto",
        my: 1,
        fontSize: "0.8rem",
      }}
    >
      {children}
    </Box>
  ),
  code: ({ children }) => (
    <Typography
      component="code"
      sx={{
        bgcolor: "action.hover",
        borderRadius: 0.5,
        px: 0.5,
        fontFamily: "monospace",
        fontSize: "0.85em",
      }}
    >
      {children}
    </Typography>
  ),
  strong: ({ children }) => (
    <Box component="strong" sx={{ fontWeight: 700 }}>
      {children}
    </Box>
  ),
};

function AiAssistant() {
  const { getAccessToken } = useKindeAuth();

  const [notConfigured, setNotConfigured] = useState(false);
  const [messages, setMessages] = useState(loadHistory);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [activeTool, setActiveTool] = useState(null);

  const abortRef = useRef(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    getAiStatus()
      .then((status) => setNotConfigured(!status.configured))
      .catch(() => setNotConfigured(false));
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, activeTool]);

  // Persist completed turns so the chat survives refreshes. In-flight
  // (streaming) messages are excluded - they're only saved once done.
  useEffect(() => {
    try {
      const persisted = messages
        .filter((m) => m.status === "done")
        .slice(-HISTORY_MAX_MESSAGES)
        .map((m) => ({ role: m.role, content: m.content }));
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(persisted));
    } catch {
      // Storage unavailable - the chat just works without persistence.
    }
  }, [messages]);

  const handleClear = () => {
    try {
      localStorage.removeItem(HISTORY_STORAGE_KEY);
    } catch {
      // Ignore - state is still cleared below.
    }
    setMessages([]);
    setInput("");
  };

  const appendToMessage = (id, updater) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === id ? updater(m) : m)),
    );
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || streaming || notConfigured) return;

    setInput("");

    const userMessage = { id: ++messageId, role: "user", content: text, status: "done" };
    const assistantMessage = {
      id: ++messageId,
      role: "assistant",
      content: "",
      status: "streaming",
      error: null,
    };
    setMessages((prev) => [...prev, userMessage, assistantMessage]);
    setStreaming(true);
    setActiveTool(null);

    const controller = new AbortController();
    abortRef.current = controller;

    // Older assistant replies can be long markdown tables - trim each to
    // its most recent 2000 chars so the history stays under the server's
    // per-message cap. The newest message always goes in full.
    const history = [...messages, userMessage]
      .filter((m) => m.status === "done")
      .slice(-12)
      .map((m, index, array) => ({
        role: m.role,
        content: index === array.length - 1 ? m.content : m.content.slice(-2000),
      }));

    try {
      const token = await getAccessToken();
      await streamAiQuery({
        messages: history,
        token,
        signal: controller.signal,
        onEvent: (event) => {
          if (event.type === "text_delta") {
            appendToMessage(assistantMessage.id, (m) => ({
              ...m,
              content: m.content + event.text,
            }));
          } else if (event.type === "tool_start") {
            setActiveTool(event.name);
          } else if (event.type === "done") {
            setActiveTool(null);
            appendToMessage(assistantMessage.id, (m) => ({
              ...m,
              status: "done",
            }));
          } else if (event.type === "error") {
            setActiveTool(null);
            appendToMessage(assistantMessage.id, (m) => ({
              ...m,
              status: "error",
              error: event.message,
            }));
          }
        },
      });
    } catch (error) {
      if (error?.name !== "AbortError") {
        setActiveTool(null);
        appendToMessage(assistantMessage.id, (m) => ({
          ...m,
          status: "error",
          error: error.message || "The AI request failed. Please try again.",
        }));
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
      inputRef.current?.focus();
    }
  };

  const handleStop = () => {
    abortRef.current?.abort();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={{ py: 4 }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            ✨ AI Assistant
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ask questions about players, entries, flags and rankings. The
            assistant can read data, and can create a flag when you
            explicitly ask it to - everything else is read-only.
          </Typography>
        </Box>
        {messages.length > 0 && (
          <IconButton
            size="small"
            onClick={handleClear}
            title="Clear chat history"
          >
            <DeleteSweep />
          </IconButton>
        )}
      </Box>

      {notConfigured && (
        <Alert severity="info" sx={{ mb: 3 }}>
          AI assistant is not configured. Add ANTHROPIC_API_KEY to the server
          .env and restart the server.
        </Alert>
      )}

      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          height: { xs: "calc(100vh - 320px)", md: "calc(100vh - 280px)" },
          minHeight: 420,
          overflow: "hidden",
        }}
      >
        {/* Messages */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {messages.length === 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Try: "who are the top 10 women in WPB" or "what flags are
                pending" or "show me Anneke Scheepers' ranking"
              </Typography>
            </Box>
          )}

          {messages.map((message) => (
            <Box
              key={message.id}
              sx={{
                alignSelf: message.role === "user" ? "flex-end" : "flex-start",
                maxWidth: "85%",
              }}
            >
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  backgroundColor:
                    message.role === "user" ? "#ffffff" : "action.hover",
                  color:
                    message.role === "user" ? "#a855f7" : "text.primary",
                  borderColor:
                    message.role === "user" ? "#a855f7" : undefined,
                  borderRadius: 2,
                }}
              >
                {message.role === "assistant" ? (
                  <Box sx={{ "& > :last-child": { mb: 0 } }}>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={markdownComponents}
                    >
                      {message.content}
                    </ReactMarkdown>
                    {message.status === "streaming" &&
                      !message.content &&
                      !activeTool && (
                        <Box component="span" sx={{ display: "inline-flex" }}>
                          <Typography variant="body2" sx={{ animation: "ai-dots 1.2s infinite" }}>
                            ●
                          </Typography>
                          <Typography variant="body2" sx={{ animation: "ai-dots 1.2s infinite 0.2s" }}>
                            ●
                          </Typography>
                          <Typography variant="body2" sx={{ animation: "ai-dots 1.2s infinite 0.4s" }}>
                            ●
                          </Typography>
                        </Box>
                      )}
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                  >
                    {message.content}
                  </Typography>
                )}
              </Paper>
              {message.status === "streaming" && activeTool && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                  Looking at {activeTool.replace(/_/g, " ")} data…
                </Typography>
              )}
              {message.status === "error" && message.error && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                  {message.error}
                </Typography>
              )}
            </Box>
          ))}
          <Box ref={bottomRef} />
        </Box>

        {/* Input */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            p: 2,
            borderTop: 1,
            borderColor: "divider",
            alignItems: "flex-end",
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={5}
            placeholder={
              notConfigured
                ? "AI assistant is not configured"
                : "Ask about rankings, players, entries or flags…"
            }
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={notConfigured}
            inputRef={inputRef}
            size="small"
          />
          {streaming ? (
            <IconButton color="error" onClick={handleStop} title="Stop">
              <Stop />
            </IconButton>
          ) : (
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={!input.trim() || notConfigured}
              title="Send"
            >
              <Send />
            </IconButton>
          )}
        </Box>
      </Paper>

      {/* Animated-dots keyframes */}
      <style>
        {`@keyframes ai-dots {
             0%, 80%, 100% { opacity: 0.25; }
             40% { opacity: 1; }
           }`}
      </style>
    </Box>
  );
}

export default AiAssistant;
